import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';

import { useQuery } from '@tanstack/react-query';

import NotificationPreferencesContainer from 'components/notificationPreferences/NotificationPreferencesContainer';
import Button from 'components/shared/Button';
import CircularProgress from 'components/shared/CircularProgress';
import Tabs, { TabType } from 'components/Tabs';
import { FormProvider } from 'context/form';
import { TranslationProvider } from 'context/translations';
import { Notification as FlexibleNotification } from 'models/flexible/Notifications';
import { Notification as OriginalNotification } from 'models/Notifications';
import { adminFetchUserNotificationPreferences, adminShowUser, adminUsersQueryKeys } from 'services/api/admin/users';

// This page is an "ambassador" — accessible from both admin and non-admin routes.
// It must accept notification data shaped by either the original or flexible model.
type AnyNotification = OriginalNotification | FlexibleNotification;
type AmbassadorFormValues = { [groupTypeId: string]: AnyNotification };
import { H2 } from 'styles/typography';

export default function AdminUserPreferences() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [selectedTab, setSelectedTab] = useState<TabType>();

  const { data: user, isLoading: userLoading } = useQuery({
    queryKey: adminUsersQueryKeys.show(id!),
    queryFn: ({ signal }) =>
      adminShowUser(id!, signal, [
        'actingClient',
        'client',
        'credential',
        'enabledProviderTypes',
        'role',
        'selectedGroups',
        'selectedProvidedProviderTypes',
        'selectedManagedProviderTypes',
      ]),
    enabled: !!id,
  });

  const { data: notificationPreferences, isLoading: prefsLoading } = useQuery({
    queryKey: adminUsersQueryKeys.notificationPreferences(id!),
    queryFn: ({ signal }) => adminFetchUserNotificationPreferences(id!, signal),
    enabled: !!id,
  });

  const tabs = useMemo(() => {
    if (!notificationPreferences) return [];

    const providerTypeIds = (user?.enabledProviderTypes ?? []).map((t) => t.id);

    const vals = notificationPreferences
      .filter((pref) => providerTypeIds.includes(pref.groupType.id))
      .map((pref) => ({
        label: pref.groupType.displayName,
        value: pref.groupType.id,
      }));

    if (!selectedTab && vals.length > 0) setSelectedTab(vals[0]);
    return vals;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notificationPreferences, user?.enabledProviderTypes]);

  const initialValues = useMemo(() => {
    return notificationPreferences?.reduce((acc, pref) => {
      acc[pref.groupType.id] = pref;
      return acc;
    }, {} as AmbassadorFormValues);
  }, [notificationPreferences]);

  if (userLoading || prefsLoading) return <CircularProgress />;
  if (!user || !notificationPreferences || !initialValues) return null;

  const actingClient = user.actingClient as any;
  const configForSelectedGroupType = actingClient?.configForGroupType(selectedTab?.value || '');
  const caseManagerEnabled = !!configForSelectedGroupType?.caseManager;
  const utilizationManagerEnabled = !!configForSelectedGroupType?.utilizationManager;

  return (
    <TranslationProvider groupType={selectedTab && (initialValues[selectedTab.value]?.groupType as any)}>
      <FormProvider<AmbassadorFormValues, object>
        initialValues={initialValues}
        onSubmit={() => {}}
        resetOnInitialValuesChange>
        <PageWrapper>
          <HeaderArea>
            <Header>
              <TitleSection>
                <Title>{user.name}</Title>
                <Subtitle>
                  {actingClient?.name} — {user.role?.name}
                </Subtitle>
              </TitleSection>
              <Button variant='ghost' onClick={() => navigate('/admin/users')}>
                Back to Users
              </Button>
            </Header>
            {tabs.length > 0 && (
              <TabContainer>
                <Tabs
                  tabs={tabs}
                  onTabClick={setSelectedTab}
                  selectedTab={tabs.find((tab) => tab.value === selectedTab?.value) || tabs[0]}
                />
              </TabContainer>
            )}
          </HeaderArea>
          <Content>
            {selectedTab && (
              <NotificationPreferencesContainer
                actingClientId={actingClient?.id || ''}
                caseManagerEnabled={caseManagerEnabled}
                utilizationManagerEnabled={utilizationManagerEnabled}
                selectedGroupType={selectedTab}
                canActAsManager={false}
                disabled
              />
            )}
          </Content>
        </PageWrapper>
      </FormProvider>
    </TranslationProvider>
  );
}

const PageWrapper = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  height: 100%;
`;

const HeaderArea = styled.div`
  background: white;
  border-bottom: 1px solid ${({ theme }) => theme.colors.black10};
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: start;
  padding: 24px;
`;

const TitleSection = styled.div``;

const Title = styled.h1`
  font-size: 24px;
  font-weight: 700;
  margin: 0;
`;

const Subtitle = styled(H2)`
  color: ${({ theme }) => theme.colors.primaryBlue};
  font-size: 16px;
  margin-top: 12px;
`;

const TabContainer = styled.div`
  padding: 0 24px;
`;

const Content = styled.div`
  padding: 24px;
`;
